/**
 * Cache Service for managing service worker cache operations
 * Provides a clean interface for components to interact with the cache
 */

// Check if service worker is supported
const isServiceWorkerSupported = 'serviceWorker' in navigator;

// Function to send messages to the service worker
const sendMessageToServiceWorker = async (message) => {
  if (!isServiceWorkerSupported) return false;
  
  const registration = await navigator.serviceWorker.ready;
  if (registration.active) {
    registration.active.postMessage(message);
    return true;
  }
  return false;
};

// Cached timestamp storage to avoid repeated network requests
const cachedTimestamps = new Map();

const cacheService = {
  /**
   * Initialize the service worker
   */
  async registerServiceWorker() {
    if (!isServiceWorkerSupported) {
      console.warn('Service workers are not supported in this browser.');
      return false;
    }
    
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service worker registered:', registration.scope);
      return true;
    } catch (error) {
      console.error('Service worker registration failed:', error);
      return false;
    }
  },
  
  /**
   * Check if a specific data is available in cache and not expired
   * @param {string} url - The URL of the cached data to check
   * @returns {Promise<boolean>} True if the data is cached and not expired
   */
  async isCached(url) {
    if (!isServiceWorkerSupported) return false;
    
    // Try to get from local memory first
    const timestamp = cachedTimestamps.get(url);
    if (timestamp) {
      return true;
    }
    
    // Otherwise check service worker cache
    try {
      const cache = await caches.open(this.getCacheNameForUrl(url));
      const response = await cache.match(url);
      
      if (!response) return false;
      
      // Get timestamp from header
      const timestamp = response.headers.get('sw-cache-timestamp');
      if (!timestamp) return false;
      
      const cachedTime = parseInt(timestamp, 10);
      const currentTime = Date.now();
      
      // Check if expired based on URL type
      const ttl = this.isPriceUrl(url) ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
      const isExpired = currentTime - cachedTime > ttl;
      
      if (!isExpired) {
        // Store in local memory for faster future checks
        cachedTimestamps.set(url, cachedTime);
      }
      
      return !isExpired;
    } catch (error) {
      console.error('Error checking cache:', error);
      return false;
    }
  },
  
  /**
   * Get the appropriate cache name for a URL
   * @param {string} url - The URL to determine cache name for
   * @returns {string} The cache name
   */
  getCacheNameForUrl(url) {
    if (this.isPriceUrl(url)) {
      return 'price-cache-v1';
    } else if (this.isSearchStructureUrl(url)) {
      return 'structure-cache-v1';
    } else {
      return 'product-cache-v1';
    }
  },
  
  /**
   * Check if a URL is for price data
   * @param {string} url - The URL to check
   * @returns {boolean} True if it's a price URL
   */
  isPriceUrl(url) {
    return url.includes('/price') || url.includes('/prices');
  },
  
  /**
   * Check if a URL is for search structure data
   * @param {string} url - The URL to check
   * @returns {boolean} True if it's a search structure URL
   */
  isSearchStructureUrl(url) {
    return url.includes('/search') && url.includes('structure=true');
  },
  
  /**
   * Invalidate price cache for a specific URL or all prices
   * @param {string} [url] - Optional URL to invalidate (if not provided, invalidates all price cache)
   * @returns {Promise<boolean>} True if the invalidation message was sent
   */
  async invalidatePriceCache(url = null) {
    // Clear from local memory first
    if (url) {
      cachedTimestamps.delete(url);
    } else {
      // Clear all price URLs from memory cache
      for (const [cachedUrl] of cachedTimestamps) {
        if (this.isPriceUrl(cachedUrl)) {
          cachedTimestamps.delete(cachedUrl);
        }
      }
    }
    
    // Send invalidation message to service worker
    return sendMessageToServiceWorker({
      type: 'INVALIDATE_CACHE',
      cacheType: 'price',
      url
    });
  },
  
  /**
   * Invalidate all caches
   * @returns {Promise<boolean>} True if the invalidation message was sent
   */
  async invalidateAllCache() {
    // Clear all from local memory
    cachedTimestamps.clear();
    
    // Send invalidation message to service worker
    return sendMessageToServiceWorker({
      type: 'INVALIDATE_CACHE',
      cacheType: 'all'
    });
  },
  
  /**
   * Get cache metadata for UI display
   * @param {string} url - The URL to get cache info for
   * @returns {Promise<Object|null>} Cache metadata or null if not cached
   */
  async getCacheMetadata(url) {
    if (!isServiceWorkerSupported) return null;
    
    try {
      const cache = await caches.open(this.getCacheNameForUrl(url));
      const response = await cache.match(url);
      
      if (!response) return null;
      
      const timestamp = response.headers.get('sw-cache-timestamp');
      if (!timestamp) return null;
      
      const cachedTime = parseInt(timestamp, 10);
      const currentTime = Date.now();
      const age = Math.floor((currentTime - cachedTime) / 1000 / 60); // age in minutes
      
      return {
        cached: true,
        cachedAt: new Date(cachedTime),
        ageInMinutes: age,
        isFresh: this.isPriceUrl(url) ? age < 60 : age < 24 * 60 // 60 min or 24 hours
      };
    } catch (error) {
      console.error('Error getting cache metadata:', error);
      return null;
    }
  }
};

export default cacheService; 